import { getGetPlatformCatalogsQueryKey, PlatformCatalogDTO, useCreatePlatformCatalog } from "@bvhi/backend-api/apis";
import { Box, LoadingButton } from "@bvhi/ui-base";
import { AddIcon } from "@bvhi/ui-icons";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, object, string } from "yup";
import { BVHINavigationBackButton } from "../../../../../components/bvhi-navigation-back-button";
import { FormBlockerDialog } from "../../../../../components/form/form-blocker-dialog";
import { FormWithActionCard } from "../../../../../components/form/form-with-action-card";
import { NavigationBar } from "../../../../../components/navigation-bar";
import { useApiErrorContext } from "../../../../../context/use-api-error-context";
import { useAuthContext } from "../../../../../context/use-auth-context";
import { pickIfSet } from "../../../../../helpers/object";
import { useToast } from "../../../../../hooks/use-toast";
import { Route } from "../../../../../routes/_main/app/catalogs.create";
import { toNarrowedTypeOrNull } from "../../../../../utils/ts";
import { CatalogsCreateCreateCatalogCard } from "./catalogs-create-create-catalog-card";
import { CatalogsCreateStepper } from "./catalogs-create-stepper";

interface Props {
  steps: string[];
  activeStep: number;
}

interface CatalogCreateFormValues {
  name: string;
  notes?: string;
}

export const CatalogsCreateCreateCatalogView = ({ steps, activeStep }: Props) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const { t } = useTranslation(["validation", "errors", "users", "common", "catalogs"]);
  const navigate = useNavigate({ from: Route.fullPath });
  const { setErrors } = useApiErrorContext();
  const { pushCreateErrorToast, pushCreateSuccessToast } = useToast();
  const { mutate: createPlatformCatalog, isPending: isCreateCatalogPending } = useCreatePlatformCatalog();

  const createCatalogSchema: AnyObjectSchema = object({
    name: string().min(1, t("validation:string.min.1")).required(t("validation:required")),
    notes: string()
      .nullable()
      .max(255, t("validation:string.max.number", { value: 255 }))
      .optional(),
  });

  const {
    formState: { isDirty, isValid, isSubmitSuccessful, ...otherFormState },
    handleSubmit,
    ...otherMethods
  } = useForm<CatalogCreateFormValues>({
    resolver: yupResolver(createCatalogSchema),
    mode: "all",
    defaultValues: {
      name: "",
      notes: "",
    },
  });

  const onSubmit: SubmitHandler<CatalogCreateFormValues> = (data) => {
    const values = pickIfSet(data, ["name", "notes"]);
    createPlatformCatalog(
      {
        supplierId: currentUser?.organisation?.id ?? "",
        data: { ...values },
      },
      {
        onSuccess: (response) => {
          void queryClient.invalidateQueries({ queryKey: getGetPlatformCatalogsQueryKey(currentUser?.organisation?.id ?? "") });
          pushCreateSuccessToast(t("catalogs:catalogs.catalog"));
          const catalog = toNarrowedTypeOrNull<PlatformCatalogDTO>(response.data, "id");
          if (catalog) {
            void navigate({
              search: { createdCatalogId: catalog.id, step: activeStep + 1 },
              replace: true,
            });
          }
        },
        onError: (error) => {
          if (error.status && [400, 403].includes(error.status)) {
            setErrors(error);
          }
          if (error.status === 500) pushCreateErrorToast(t("users:users.user"));
        },
      }
    );
  };

  return (
    <FormProvider formState={{ isDirty, isValid, isSubmitSuccessful, ...otherFormState }} handleSubmit={handleSubmit} {...otherMethods}>
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        <>
          <NavigationBar
            actions={[<BVHINavigationBackButton key={0} fallbackNavigateOptions={{ to: "/app/catalogs" }} />]}
            showBreadcrumbs={true}
          />
          <Box py={4}>
            <CatalogsCreateStepper activeStep={activeStep} steps={steps} />
          </Box>

          <FormWithActionCard
            contained
            actionCardContent={
              <LoadingButton disabled={!isDirty} loading={isCreateCatalogPending} startIcon={<AddIcon />} type="submit">
                {t("catalogs:catalogs.button.create.catalog")}
              </LoadingButton>
            }
          >
            <CatalogsCreateCreateCatalogCard />
          </FormWithActionCard>
        </>
      </form>
      <FormBlockerDialog isDirty={isDirty && !isSubmitSuccessful} />
    </FormProvider>
  );
};
